import {
  iMessage,
  iMessageAttachment,
} from "@sdk/conversations/conversations.models";
import { useSDKActionWithDeps } from "@sdk/sdk.hooks";
import { iWACallLog } from "@sdk/wa-call-logs/wa-call-logs-model";
import { Card, Typography, message } from "antd";
import { LoadingIndicatorWithoutSpin } from "components/common/loading-indicator/loading-indicator";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { selectCurrentUserId } from "store/modules/users/users.selectors";
import { loadWACallLogById } from "store/modules/wa-call-logs/wa-call-logs.helpers";
import { selectWACallLogById } from "store/modules/wa-call-logs/wa-call-logs.selectors";
import { useQueryWithStore } from "store/store.hooks";
import { isWACallLogActive } from "./call-status";
import { AudioDeviceSelector } from "./components/audio-device-selector";
import { IncomingWACallContactDetails } from "./components/incoming-wa-call-contact-details";
import { IncomingWACallControls } from "./components/incoming-wa-call-controls";
import { IncomingWACallStatus } from "./components/incoming-wa-call-status";
import { OutgoingWACallControls } from "./components/outgoing-wa-call-controls";
import { OutgoingWACallStatus } from "./components/outgoing-wa-call-status";
import { useWebRTCIncomingWACall } from "./hooks/use-web-rtc-incoming-wa-call";
import { useWebRTCOutgoingWACall } from "./hooks/use-web-rtc-outgoing-wa-call";

const { Text, Title } = Typography;

export const WhatsappCallAttachment = ({
  attachment,
  message,
}: {
  attachment: iMessageAttachment;
  message: iMessage;
}) => {
  const callLogId = attachment.payload.callLogId as string;

  if (attachment?.payload?.outgoingCall) {
    return (
      <WhatsAppOutgoingCallCard
        callLogId={callLogId}
        browserId={attachment.payload.browserId as string}
        messageId={message.id!}
        conversationId={message.conversationId}
      />
    );
  }

  if (!callLogId) {
    return null;
  }

  return <WhatsAppIncomingCallCard callLogId={callLogId} />;
};

export const WhatsAppIncomingCallCard = ({
  callLogId,
}: {
  callLogId: string;
}) => {
  const { state: waCallLog, isLoading: isLoading } = useQueryWithStore(
    selectWACallLogById(callLogId),
    loadWACallLogById(callLogId)(),
    [callLogId],
    !callLogId,
  );

  if (isLoading) {
    return <LoadingIndicatorWithoutSpin />;
  }

  if (!waCallLog) {
    return null;
  }

  if (isWACallLogActive(waCallLog)) {
    return <WhatsappIncomingCallContainer callLog={waCallLog} />;
  }

  return (
    <Card size="small" className="mb-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <i className="ri-phone-line text-red-500"></i>
          <Text type="secondary">Call ended</Text>
        </div>
        <Text type="secondary" className="text-xs">
          {waCallLog.duration
            ? `${Math.floor(waCallLog.duration / 60)}:${(
                waCallLog.duration % 60
              )
                .toString()
                .padStart(2, "0")}`
            : ""}
        </Text>
      </div>
    </Card>
  );
};

export const WhatsappIncomingCallContainer = ({
  callLog,
}: {
  callLog: iWACallLog;
}) => {
  const data = callLog.callData!;
  const userId = useSelector(selectCurrentUserId);
  const recordingConfig = useMemo(
    () =>
      callLog.id && userId
        ? {
            organizationId: callLog.organizationId,
            conversationId: callLog.conversationId,
            callId: callLog.id,
            userId,
          }
        : null,
    [callLog.id, callLog.organizationId, callLog.conversationId, userId],
  );

  const [callState, setCallState] = useState<
    "incoming" | "connecting" | "connected" | "ended"
  >("incoming");
  const [connectionId, setConnectionId] = useState<string>("");
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const {
    peerConnection,
    localStream,
    remoteStream,
    isMuted,
    isSpeakerOn,
    acceptCall,
    rejectCall,
    terminateCall,
    toggleMute,
    toggleSpeaker,
    setAudioInputDevice,
    setAudioOutputDevice,
    audioInputDevices,
    audioOutputDevices,
    selectedAudioInput,
    selectedAudioOutput,
    callDuration,
    isProcessing,
  } = useWebRTCIncomingWACall(data, recordingConfig);

  // Effect to manage audio element
  useEffect(() => {
    if (audioRef.current && remoteStream) {
      audioRef.current.srcObject = remoteStream;
      // Set initial mute state based on speaker state
      audioRef.current.muted = !isSpeakerOn;
      if (isSpeakerOn) {
        audioRef.current.volume = 1.0;
      }
    }
  }, [remoteStream, isSpeakerOn]);

  // Effect to manage speaker state
  useEffect(() => {
    if (audioRef.current) {
      if (isSpeakerOn) {
        audioRef.current.muted = false;
        audioRef.current.volume = 1.0;
      } else {
        audioRef.current.muted = true;
      }
    }
  }, [isSpeakerOn]);

  const { doAction: acceptWACall, isProcessing: isAcceptingCall } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => async () => {
          setCallState("connecting");
          const sdpAnswer = await acceptCall();
          // console.log("sdpAnswer", sdpAnswer);
          const finalWaSdp = sdpAnswer.replace(
            "a=setup:actpass",
            "a=setup:active",
          );
          return SDK.connections.acceptWACall({
            callLogId: callLog.id!,
            sdpAnswer: finalWaSdp,
          });
        },
        failureMessage: "Failed to accept call",
        successMessage: "Call accepted",
      }),
      [acceptCall, callLog.id],
    );

  const { doAction: rejectWACall, isProcessing: isRejectingCall } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => async () => {
          setCallState("ended");
          await rejectCall();
          return SDK.connections.rejectWACall({
            callLogId: callLog.id!,
          });
        },
        failureMessage: "Failed to reject call",
        successMessage: "Call rejected",
      }),
      [rejectCall, callLog.id],
    );

  const { doAction: terminateWACall, isProcessing: isTerminatingCall } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => async () => {
          setCallState("ended");
          await terminateCall();
          return SDK.connections.terminateWACall({
            callLogId: callLog.id!,
          });
        },
        failureMessage: "Failed to terminate call",
        successMessage: "Call terminated",
      }),
      [terminateCall, callLog.id],
    );

  const handleAcceptCall = async () => {
    try {
      await acceptWACall();
      setCallState("connected");
    } catch (error) {
      setCallState("incoming");
      message.error("Failed to accept call");
    }
  };

  const handleRejectCall = async () => {
    try {
      await rejectWACall();
    } catch (error) {
      message.error("Failed to reject call");
    }
  };

  const handleTerminateCall = async () => {
    try {
      await terminateWACall();
    } catch (error) {
      message.error("Failed to terminate call");
    }
  };

  return (
    <Card size="small" className="mb-2">
      <div className="space-y-3">
        {/* Call Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <i
              className={`ri-phone-line ${
                callState === "connected"
                  ? "text-green-500"
                  : callState === "connecting"
                  ? "text-yellow-500"
                  : "text-red-500"
              }`}
            ></i>
            <Title level={5} className="mb-0">
              WhatsApp Call
            </Title>
          </div>
          <IncomingWACallStatus
            state={callState}
            duration={callDuration}
            direction={data.direction}
          />
        </div>

        {/* Contact Info */}
        <IncomingWACallContactDetails
          contactId={callLog.contactId}
          connectionId={callLog.connectionId}
          callData={data}
        />

        {/* Audio Device Selection - Only show when call is connected */}
        {callState === "connected" && (
          <div className="space-y-2">
            <AudioDeviceSelector
              audioInputDevices={audioInputDevices}
              audioOutputDevices={audioOutputDevices}
              selectedAudioInput={selectedAudioInput}
              selectedAudioOutput={selectedAudioOutput}
              onAudioInputChange={setAudioInputDevice}
              onAudioOutputChange={setAudioOutputDevice}
            />
          </div>
        )}

        {/* Call Controls */}
        <IncomingWACallControls
          callState={callState}
          isMuted={isMuted}
          isSpeakerOn={isSpeakerOn}
          isProcessing={
            isProcessing ||
            isAcceptingCall ||
            isRejectingCall ||
            isTerminatingCall
          }
          onAccept={handleAcceptCall}
          onReject={handleRejectCall}
          onTerminate={handleTerminateCall}
          onToggleMute={toggleMute}
          onToggleSpeaker={toggleSpeaker}
        />

        {/* Audio Elements */}
        {remoteStream && (
          <audio ref={audioRef} autoPlay style={{ display: "none" }} />
        )}
      </div>
    </Card>
  );
};

export const WhatsAppOutgoingCallCard = ({
  conversationId,
  messageId,
  callLogId,
  browserId,
}: {
  conversationId: string;
  /** Optional when rendering from FloatingWhatsAppCallContainer (answer-only flow). */
  messageId?: string;
  callLogId?: string;
  browserId: string;
}) => {
  const { state: waCallLog, isLoading: isLoading } = useQueryWithStore(
    selectWACallLogById(callLogId || ""),
    loadWACallLogById(callLogId || "")(),
    [callLogId],
    !callLogId,
  );

  // If call is completed or rejected, show ended state
  // Only show this if we have a callLog (not during initial loading)
  if (waCallLog && !isWACallLogActive(waCallLog)) {
    return (
      <Card size="small" className="mb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <i className="ri-phone-line text-red-500"></i>
            <Text type="secondary">Call ended</Text>
          </div>
          <Text type="secondary" className="text-xs">
            {waCallLog.duration
              ? `${Math.floor(waCallLog.duration / 60)}:${(
                  waCallLog.duration % 60
                )
                  .toString()
                  .padStart(2, "0")}`
              : ""}
          </Text>
        </div>
      </Card>
    );
  }

  // Always render the container to prevent unmounting/remounting
  // This ensures pcRef persists across callLogId transitions
  return (
    <WhatsappOutgoingCallContainer
      callLog={waCallLog}
      conversationId={conversationId}
      messageId={messageId}
      browserId={browserId}
    />
  );
};

export const WhatsappOutgoingCallContainer = ({
  callLog,
  conversationId,
  messageId,
  browserId,
}: {
  callLog?: iWACallLog;
  conversationId: string;
  /** Optional when rendering from FloatingWhatsAppCallContainer (answer-only flow). Initiate is disabled when undefined. */
  messageId?: string;
  browserId: string;
}) => {
  const data = callLog?.callData;
  const userId = useSelector(selectCurrentUserId);
  const recordingConfig = useMemo(
    () =>
      callLog?.id && userId
        ? {
            organizationId: callLog.organizationId,
            conversationId: callLog.conversationId ?? conversationId,
            callId: callLog.id,
            userId,
          }
        : null,
    [
      callLog?.id,
      callLog?.organizationId,
      callLog?.conversationId,
      conversationId,
      userId,
    ],
  );

  const [callState, setCallState] = useState<
    "outgoing_init" | "connecting" | "connected" | "ended"
  >(
    (() => {
      // If no callLog, we're in init state
      if (!callLog) {
        return "outgoing_init";
      }
      // Initialize state based on call log status
      if (callLog.callStatus === "OUTGOING_INIT") {
        return "outgoing_init";
      } else if (
        callLog.callStatus === "OUTGOING_INIT_SUCCESS" ||
        callLog.callStatus === "RINGING"
      ) {
        return "connecting";
      } else if (callLog.callStatus === "IN_PROGRESS") {
        return "connected";
      } else {
        return "ended";
      }
    })(),
  );

  const audioRef = useRef<HTMLAudioElement | null>(null);

  const {
    peerConnection,
    localStream,
    remoteStream,
    isMuted,
    isSpeakerOn,
    initiateCall,
    rejectCall,
    terminateCall,
    toggleMute,
    toggleSpeaker,
    setAudioInputDevice,
    setAudioOutputDevice,
    audioInputDevices,
    audioOutputDevices,
    selectedAudioInput,
    selectedAudioOutput,
    callDuration,
    isProcessing,
  } = useWebRTCOutgoingWACall(data, recordingConfig);

  // Update call state based on call log status changes
  useEffect(() => {
    if (!callLog) {
      return;
    }
    if (callLog.callStatus === "OUTGOING_INIT") {
      setCallState("outgoing_init");
    } else if (
      callLog.callStatus === "OUTGOING_INIT_SUCCESS" ||
      callLog.callStatus === "RINGING"
    ) {
      setCallState("connecting");
    } else if (callLog.callStatus === "IN_PROGRESS") {
      setCallState("connected");
    } else if (
      callLog.callStatus === "COMPLETED" ||
      callLog.callStatus === "REJECTED"
    ) {
      setCallState("ended");
    }
  }, [callLog]);

  // Effect to manage audio element
  useEffect(() => {
    if (audioRef.current && remoteStream) {
      audioRef.current.srcObject = remoteStream;
      // Set initial mute state based on speaker state
      audioRef.current.muted = !isSpeakerOn;
      if (isSpeakerOn) {
        audioRef.current.volume = 1.0;
      }
    }
  }, [remoteStream, isSpeakerOn]);

  // Effect to manage speaker state
  useEffect(() => {
    if (audioRef.current) {
      if (isSpeakerOn) {
        audioRef.current.muted = false;
        audioRef.current.volume = 1.0;
      } else {
        audioRef.current.muted = true;
      }
    }
  }, [isSpeakerOn]);

  const { doAction: initiateWACall, isProcessing: isInitiatingCall } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => async () => {
          if (messageId == null) return;
          setCallState("connecting");
          const sdpOffer = await initiateCall();
          return SDK.connections.initiateCall({
            conversationId,
            messageId,
            sdpOffer,
          });
        },
        failureMessage: "Failed to initiate call",
        successMessage: "Call initiated",
        // Reset UI if backend rejects (e.g. missing identity) — identity-aware
        // copy comes from processServerError.
        onFailed: () => setCallState("outgoing_init"),
      }),
      [conversationId, initiateCall, messageId],
    );

  const { doAction: terminateWACall, isProcessing: isTerminatingCall } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => async () => {
          setCallState("ended");
          await terminateCall();
          if (callLog?.id) {
            return SDK.connections.terminateWACall({
              callLogId: callLog.id,
            });
          }
        },
        failureMessage: "Failed to terminate call",
        successMessage: "Call terminated",
      }),
      [terminateCall, callLog?.id],
    );

  const handleInitiateCall = useCallback(async () => {
    try {
      await initiateWACall();
    } catch (error) {
      setCallState("outgoing_init");
      message.error("Failed to initiate call");
    }
  }, [initiateWACall]);

  const handleCancelCall = useCallback(async () => {
    try {
      setCallState("ended");
      await rejectCall();
      if (callLog?.id) {
        await terminateWACall();
      }
    } catch (error) {
      message.error("Failed to cancel call");
    }
  }, [rejectCall, terminateWACall, callLog?.id]);

  const handleTerminateCall = useCallback(async () => {
    try {
      await terminateWACall();
    } catch (error) {
      message.error("Failed to terminate call");
    }
  }, [terminateWACall]);

  // Auto-initiate call if needed (when callLogId is undefined, browserId matches, and we have messageId)
  useEffect(() => {
    console.log(
      "messageId",
      messageId,
      callState,
      !callLog,
      browserId === (window as any).browserSessionId,
    );
    if (
      messageId != null &&
      callState === "outgoing_init" &&
      !callLog &&
      browserId === (window as any).browserSessionId
    ) {
      console.log("initiating call");
      handleInitiateCall();
    }
  }, [messageId, callState, callLog, browserId, handleInitiateCall]);

  // Show loading indicator during auto-init (when callLog is undefined)
  if (!callLog && callState === "outgoing_init") {
    return <LoadingIndicatorWithoutSpin />;
  }

  return (
    <Card size="small" className="mb-2">
      <div className="space-y-3">
        {/* Call Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <i
              className={`ri-phone-line ${
                callState === "connected"
                  ? "text-green-500"
                  : callState === "connecting"
                  ? "text-yellow-500"
                  : callState === "ended"
                  ? "text-red-500"
                  : "text-orange-500"
              }`}
            ></i>
            <Title level={5} className="mb-0">
              WhatsApp Call
            </Title>
          </div>
          <OutgoingWACallStatus
            state={callState}
            duration={callDuration}
            direction={data?.direction}
          />
        </div>

        {/* Contact Info — prefer name/@username over raw From/To (may be BSUID) */}
        {callLog && (
          <IncomingWACallContactDetails
            contactId={callLog.contactId}
            connectionId={callLog.connectionId}
            callData={data}
          />
        )}

        {/* Audio Device Selection - Only show when call is connected */}
        {callState === "connected" && (
          <div className="space-y-2">
            <AudioDeviceSelector
              audioInputDevices={audioInputDevices}
              audioOutputDevices={audioOutputDevices}
              selectedAudioInput={selectedAudioInput}
              selectedAudioOutput={selectedAudioOutput}
              onAudioInputChange={setAudioInputDevice}
              onAudioOutputChange={setAudioOutputDevice}
            />
          </div>
        )}

        {/* Call Controls - Don't show init controls during auto-init. Initiate only when messageId is set (e.g. from message list, not floating). */}
        {callLog && (
          <OutgoingWACallControls
            callState={callState}
            isMuted={isMuted}
            isSpeakerOn={isSpeakerOn}
            isProcessing={isProcessing || isInitiatingCall || isTerminatingCall}
            onInitiate={messageId != null ? handleInitiateCall : undefined}
            onCancel={handleCancelCall}
            onTerminate={handleTerminateCall}
            onToggleMute={toggleMute}
            onToggleSpeaker={toggleSpeaker}
          />
        )}

        {/* Audio Elements */}
        {remoteStream && (
          <audio ref={audioRef} autoPlay style={{ display: "none" }} />
        )}
      </div>
    </Card>
  );
};
